import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from './services/auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  // The backend revokes the refresh token it just spent, so a second
  // concurrent refresh would send a dead one and end the session. Every 401
  // waiting at once shares this one call.
  private refreshing: Observable<string> | null = null;

  constructor(private router: Router, private auth: AuthService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = localStorage.getItem('token');

    const authReq = token
      ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : req;

    return next.handle(authReq).pipe(
          catchError((error: HttpErrorResponse) => {
        // A 401 on a call we signed means the access token is gone — expired
        // or revoked, which the guard's exp check cannot see. /authorization/*
        // answers 401 for a bad sign-in and for a dead refresh token; retrying
        // those would loop.
        if (error.status !== 401 || !token || authReq.url.includes('/authorization/')) {
          // Error display belongs to ErrorInterceptor; doing it here too
          // put the same message on screen twice.
          return throwError(() => error);
        }

        if (!this.auth.getRefreshToken()) {
          this.toLogin();
          return throwError(() => error);
        }

        return this.refresh().pipe(
          catchError(() => {
            this.toLogin();
            return throwError(() => error);
          }),
          // Retried through `next` rather than the interceptor, so a 401 on
          // the retry is final — it cannot trigger a second refresh.
          switchMap(fresh =>
            next.handle(req.clone({ setHeaders: { Authorization: `Bearer ${fresh}` } }))
          )
        );
      })
    );
  }

  private toLogin(): void {
    const returnUrl = this.router.url;
    this.auth.clearTokens();
    this.router.navigate(['/login'], { queryParams: { returnUrl } });
  }

  private refresh(): Observable<string> {
    this.refreshing ??= this.auth.refresh().pipe(
      finalize(() => (this.refreshing = null)),
      shareReplay(1)
    );
    return this.refreshing;
  }
}

